import axios from 'axios';
import { CLOUD_SERVICE_BASE_URL } from '@/utils/const';
import { getServiceConfigHeaders } from '@/utils/requestConfig';
import { attachTokenRefresh } from '@/utils/authInterceptors';

// Create axios instance for pricing/markup API
const pricingService = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === 'production'
    ? '/pricing'
    : '/pricing/staging'
  )
});

// Create axios instance for inventory/file service (same as VA Portal educationCenterRequest)
const inventoryService = axios.create({
  baseURL: process.env.VUE_APP_ENV === 'production'
    ? 'https://www.vibrant-america.com/lisapi/v1/lis/inventory-prod-http'
    : 'https://www.vibrant-america.com/lisapi/v1/lis/inventory-dev-http'
});

// Create axios instance for portal/order API (for customer list)
const portalOrderService = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === 'production'
    ? '/portal/order'
    : '/portal/order/staging'
  )
});

pricingService.interceptors.request.use(
  async function (config) {
    // Use the same auth headers as orderTestServiceCloud for consistency
    config.headers = await getServiceConfigHeaders();

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

pricingService.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('Pricing API error:', error);
    if (error.response?.status === 401) {
      console.warn('Unauthorized - possibly token issue');
    }
    return Promise.reject(error);
  }
);

// Inventory service interceptor
inventoryService.interceptors.request.use(
  async function (config) {
    // Use the same auth headers as orderTestServiceCloud for consistency
    config.headers = await getServiceConfigHeaders();
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

portalOrderService.interceptors.request.use(
  async function (config) {
    // Use the same auth headers as orderTestServiceCloud for consistency
    config.headers = await getServiceConfigHeaders();
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

attachTokenRefresh(pricingService);
attachTokenRefresh(inventoryService);
attachTokenRefresh(portalOrderService);

/**
 * Get all VA/VW tests and discount panels
 * @param {string} currency - currency code (default: 'usd')
 * @param {boolean} addOnTubeInfo - include tube info
 * @returns {Promise} API response with vaTests, vwTests, discountPanels, etc.
 */
export function getAllTestsAndDiscountPanels(currency = 'usd', addOnTubeInfo = false) {
  let url = `/item/price/getAllTestsAndDiscountPanels/${currency}`;
  if (addOnTubeInfo) {
    url += '?addon=tubeInfo';
  }
  return pricingService({
    url,
    method: 'get',
  });
}

/**
 * Get all promotions
 * @param {string} currency - currency code (default: 'usd')
 * @returns {Promise} API response with promotions
 */
export function getPromotions(currency = 'usd') {
  return pricingService({
    url: `/item/price/getAllPromotions/${currency}`,
    method: 'get',
  });
}

/**
 * Get all custom panels
 * @param {string} currency - currency code
 * @param {string} clinicId - clinic ID
 * @param {string} customerId - customer ID
 * @param {string} accountType - 'clinic' or 'customer'
 * @returns {Promise} API response with custom panels
 */
export function getCustomPanels(currency = 'usd', clinicId, customerId, accountType) {
  return pricingService({
    url: `/item/price/getAllCustomPanels/${currency}`,
    method: 'get',
    params: {
      clinic_id: clinicId,
      customer_id: customerId,
      account_type: accountType
    }
  });
}

/**
 * Get shortcuts (favorites)
 * @param {Object} params - query parameters
 * @param {string} params.clinic_id - clinic ID
 * @param {string} params.customer_id - customer ID
 * @param {string} params.account_type - 'clinic' or 'customer'
 * @returns {Promise} API response with shortcuts
 */
export function getShortcuts(params) {
  return pricingService({
    url: '/item/catalog/shortcut',
    method: 'get',
    params
  });
}

/**
 * Get markup schemes for pricing
 * @param {Object} data - request body
 * @param {string} data.account_type - 'clinic' or 'customer'
 * @param {number} data.account_id - account ID
 * @param {Array<number>} data.affiliate_ids - provider IDs
 * @returns {Promise} API response
 */
export function getMarkUps(data) {
  return pricingService({
    url: '/markup/getMarkupSchemes',
    method: 'post',
    data
  });
}

/**
 * Get order markup schemes for pricing
 * @param {Object} data - request body
 * @returns {Promise} API response
 */
export function getOrderMarkupSchemesApi(data) {
  return pricingService({
    url: '/markup/getOrderMarkupSchemes',
    method: 'post',
    data
  });
}

/**
 * Get all merchandise for order
 * @param {Object} params - query parameters
 * @returns {Promise} API response
 */
export function getAllMerchandisesForOrder(params) {
  return pricingService({
    url: '/merchandise/getAllMerchandisesForOrder',
    method: 'get',
    params
  });
}

/**
 * Get all merchandises (grouped by affiliate ID)
 * @param {Object} params - { account_type, customer_id, clinic_id, account_id }
 * @returns {Promise} API response
 */
export function getAllMerchandises(params) {
  return pricingService({
    url: '/merchandise/getAllMerchandises',
    method: 'get',
    params
  });
}

/**
 * Create a new merchandise
 * @param {Object} data - merchandise payload
 * @returns {Promise} API response
 */
export function createMerchandise(data) {
  return pricingService({
    url: '/merchandise/createMerchandise',
    method: 'post',
    data
  });
}

/**
 * Update an existing merchandise
 * @param {Object} data - merchandise payload with id
 * @returns {Promise} API response
 */
export function updateMerchandise(data) {
  return pricingService({
    url: '/merchandise/updateMerchandise',
    method: 'post',
    data
  });
}

/**
 * Edit merchandise status (activate/deactivate/reassign)
 * @param {string} actionType - action type
 * @param {Object} data - payload
 * @returns {Promise} API response
 */
export function editMerchandise(actionType, data) {
  return pricingService({
    url: `/merchandise/editMerchandise/${actionType}`,
    method: 'patch',
    data
  });
}

/**
 * Delete a merchandise (draft)
 * @param {Object} params - { clinic_id, customer_id, account_type, merchandise_id }
 * @returns {Promise} API response
 */
export function deleteMerchandise(params) {
  return pricingService({
    url: '/merchandise/deleteMerchandise',
    method: 'delete',
    params
  });
}

/**
 * Get initialization info for creating merchandise
 * @param {Object} params - { clinic_id, customer_id, account_type }
 * @returns {Promise} API response
 */
export function getInitializationOfCreateMerchandise(params) {
  return pricingService({
    url: '/merchandise/getInitializationOfCreateMerchandise',
    method: 'get',
    params
  });
}

/**
 * Get a single merchandise for editing
 * @param {Object} params - includes merchandise_id
 * @returns {Promise} API response
 */
export function getSingleMerchandise(params) {
  return pricingService({
    url: '/merchandise/getSingleMerchandise',
    method: 'get',
    params
  });
}

/**
 * Get merchandise order info (revenue/sold stats)
 * @param {Object} data - { item_ids: [...] }
 * @returns {Promise} API response
 */
export function merchandiseOrderInfo(data) {
  return portalOrderService({
    url: '/orderTest/merchandiseOrderInfo',
    method: 'post',
    data
  });
}

/**
 * Update markup schemes
 * @param {Object} data - request body with markup_schemes
 * @returns {Promise} API response
 */
export function postMarkUps(data) {
  return pricingService({
    url: '/markup/updateMarkupSchemes',
    method: 'post',
    data
  });
}

/**
 * Get JWT token customer list (providers for clinic)
 * @returns {Promise} API response with customer list
 */
export function getJwtTokenCustomerList() {
  return portalOrderService({
    url: '/customer/getJwtTokenCustomerList',
    method: 'get'
  });
}

/**
 * Download a merch product image file
 * @param {string} folderName - folder name (e.g. 'merch_12345')
 * @param {string} filename - file name
 * @returns {Promise} API response with blob data
 */
export function downloadFile(folderName, filename) {
  return inventoryService({
    url: `/files/${folderName}/download`,
    method: 'get',
    params: { filename },
    responseType: 'blob',
  });
}

/**
 * Upload a merch product image file
 * @param {string} folderName - folder name (e.g. 'merch_12345')
 * @param {FormData} formData - form data with file
 * @returns {Promise} API response
 */
export function uploadFile(folderName, formData) {
  return inventoryService({
    url: `/files/${folderName}/upload`,
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

/**
 * Delete a merch product image file
 * @param {string} folderName - folder name
 * @param {string} filename - file name
 * @returns {Promise} API response
 */
export function deleteFile(folderName, filename) {
  return inventoryService({
    url: `/files/${folderName}/delete`,
    method: 'delete',
    params: { filename },
  });
}

export default {
  getAllTestsAndDiscountPanels,
  getPromotions,
  getCustomPanels,
  getShortcuts,
  getMarkUps,
  getOrderMarkupSchemesApi,
  getAllMerchandisesForOrder,
  getAllMerchandises,
  createMerchandise,
  updateMerchandise,
  editMerchandise,
  deleteMerchandise,
  getInitializationOfCreateMerchandise,
  getSingleMerchandise,
  merchandiseOrderInfo,
  postMarkUps,
  getJwtTokenCustomerList,
  downloadFile,
  uploadFile,
  deleteFile
};
